/** @format */

import React, { useState, useEffect, useContext } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Button from '../Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
// import Tooltip from '@material-ui/core/Tooltip';
import { LIST_ENUMS } from '../../common/graphql/ListEnums';
import { useQuery, useMutation, useApolloClient } from 'react-apollo-hooks';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
import omit from 'lodash/omit';
import gql from 'graphql-tag';
import { CREATE_MEMBER } from '../../common/graphql/CreateMember';
import { UPDATE_MEMBER } from '../../common/graphql/UpdateMember';
import { GET_MEMBER_WITH_COGNITO_USER } from '../../common/graphql/GetMember';
import { GET_STORE } from '../../common/graphql/GetStore';
import UserContext from '../../contexts/UserContext';
import { CREATE_COGNITO_USER } from '../../common/graphql/CreateCognitoUser';
import { LIST_ROLES } from '../../common/graphql/ListRoles';
import { LIST_STORES } from '../../common/graphql/ListStores';
import { createListManagersQuery } from '../../common/graphql/ListManagers';
import { Map } from 'immutable';
import LinearProgress from '@material-ui/core/LinearProgress';
import TextFieldWithTooltip from './TextFieldWithToolTip';
import StatusButton from './StatusButton';
import { managerGroups } from '../../config';
import Message from '../Message';
import { Alerts } from 'amplify-model-react';

const styles = () => ({
  root: {
    // padding: 10,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridColumnGap: '20px',
    gridRowGap: '20px',
    alignItems: 'center'
  },
  progress: {
    marginBottom: 20
  },
  firstName: {
    gridRow: 1
  },
  lastName: {
    gridRow: 1
  },
  actionButton: {
    // gridColumn: '1 / span 2',
    placeSelf: 'center'
  }
});

function useQueryEnums() {
  const { data: enumsData } = useQuery(LIST_ENUMS.query, {
    fetchPolicy: 'network-only'
  });
  const { data: storesData } = useQuery(LIST_STORES.query, {
    fetchPolicy: 'network-only'
  });
  const { data: rolesData } = useQuery(LIST_ROLES.query, {
    fetchPolicy: 'network-only'
  });

  const { currentUser } = React.useContext(UserContext);
  const managerRoleIds = React.useMemo(() => {
    return get(currentUser, 'roles', [])
      .filter(role => {
        return managerGroups.includes(role.group);
      })
      .map(role => role.id);
  }, [currentUser]);

  const { query } = createListManagersQuery(managerRoleIds);
  const { data: managersData } = useQuery(query, {
    fetchPolicy: 'network-only'
  });

  const stores = get(storesData, 'listStores.items', []);
  const roles = get(rolesData, 'listRoles.items', []);
  const managers = get(managersData, 'listMembers.items', []);
  const employmentTypes = get(enumsData, 'employmentType.enumValues', []);
  const employmentStatus = get(enumsData, 'employmentStatus.enumValues', []);

  const allowedRoles = React.useMemo(() => {
    const myRolePrecedence = get(currentUser, 'memberInfo.role.precedence', 99999);
    return roles.filter(f => {
      return f.precedence > myRolePrecedence;
    });
  }, [roles, currentUser]);

  return {
    stores,
    roles: allowedRoles,
    managers,
    employmentTypes,
    employmentStatus
  };
}

const UserForm = ({
  memberId,
  classes,
  // open, handleClose,
  setMessage,
  onSubmit
}) => {
  const editMode = !!memberId;
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser } = useContext(UserContext);
  const [state, setState] = React.useState({ memberNo: null });

  const alertFn = Alerts.useConfirmAsync();

  const [formData, setFormData] = useState(Map({}));

  const apolloClient = useApolloClient();
  const { data: existingMemberData, networkStatus: n1 } = useQuery(
    GET_MEMBER_WITH_COGNITO_USER.query,
    {
      skip: !memberId,
      variables: {
        memberId
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'ignore',
      notifyOnNetworkStatusChange: true
    }
  );
  const isFetchingMemberData = [1, 4].includes(n1); //if network status is refetching or loading

  const { stores, roles, managers, employmentTypes, employmentStatus } = useQueryEnums();

  const saveMemberDetails = useMutation(
    editMode ? UPDATE_MEMBER.mutation : CREATE_MEMBER.mutation,
    {
      refetchQueries: () => [GET_MEMBER_WITH_COGNITO_USER.key, GET_STORE.key]
    }
  );

  const createTempPassword = () => {
    setFormData(oldFormData =>
      oldFormData.setIn(
        ['user', 'password'],
        Math.random()
          .toString(36)
          .substr(2, 9)
      )
    );
  };
  const createCognitoUser = useMutation(CREATE_COGNITO_USER.mutation);

  const selectedRoleId = formData.getIn(['member', 'memberRoleId']);

  const selectedRole = React.useMemo(() => {
    console.log('computing role..');
    return roles.find(currentRole => currentRole.id === selectedRoleId);
  }, [roles, selectedRoleId]);

  // const isCurrentUserAdmin = get(currentUser, 'memberInfo.role.group') === 'Admins';
  const disableManager = ['Admins'].includes(get(selectedRole, 'group'));
  const disableStore = disableManager;
  const memberModel = get(existingMemberData, 'getMember', {});

  async function validateMemberNo() {
    if (!state.memberNo) {
      await alertFn({
        content: (
          <div style={{ color: 'red' }}>
            ⚠ Please specify <strong>Member Number</strong>
          </div>
        ),
        title: 'Member Number should not be empty'
      });
      return false;
    }
    console.log('>>CreateUserDialog/UserForm::', 'memberModel', memberModel); //TRACE
    if (memberModel.memberNo !== state.memberNo) {
      //check if memberNo exisiting
      const checks = await apolloClient.query({
        query: gql`
          query($memNo: Int!) {
            searchMembers(filter: { memberNo: { eq: $memNo } }) {
              items {
                id
                memberNo
                firstName
                middleName
                lastName
              }
            }
          }
        `,
        variables: {
          memNo: state.memberNo
        },
        fetchPolicy: 'network-only'
      });

      if (get(checks, 'data.searchMembers.items.length', 0) < 1) {
        return true;
      } else if (get(checks, 'data.searchMembers.items.0.id') === memberModel.id) {
        return true;
      } else if (
        Number(get(checks, 'data.searchMembers.items[0].memberNo')) ===
        Number(state.memberNo)
      ) {
        const existingUser = checks.data.searchMembers.items[0];

        await alertFn({
          content: (
            <div style={{ color: 'red' }}>
              ⚠ That member number is already in use by{' '}
              {`${existingUser.firstName} ${existingUser.middleName || ''} ${
                existingUser.lastName
              }`}
            </div>
          ),
          title: 'Invalid member number.'
        });

        return false;
      } else {
        return false;
      }
    }
    return true;
  }

  const handlers = {
    onFieldChange(e) {
      if (!e.target) return;
      const { id, name, value } = e.target;
      const fieldKey = id || name;
      setFormData(oldFormData => oldFormData.setIn(fieldKey.split('.'), value));
    },
    handleSubmit: async e => {
      e.preventDefault();
      // if (!role || !status || !employmentType || !store) {
      //   setMessage({ type: 'warning', content: 'Please fill up all fields.', show: true });
      //   return;
      // }
      if ((await validateMemberNo()) === false) return;

      setIsLoading(true);
      try {
        let id = memberId,
          email = '';
        if (!editMode) {
          const response = await createCognitoUser({
            variables: {
              Username: formData.getIn(['user', 'email']),
              TemporaryPassword: formData.getIn(['user', 'password']),
              Group: selectedRole.group
            }
          });
          console.log('COG USER CREATED: ', response);
          id = get(response, 'data.createCognitoUser.Username');
          email = get(response, 'data.createCognitoUser.Email');
        }
        const memberData = formData.get('member').toJS();
        await saveMemberDetails({
          variables: {
            input: {
              id,
              email,
              ...memberData,
              memberNo: state.memberNo
            }
          }
        });
        await apolloClient.mutate({
          mutation: gql`
            mutation($memberId: ID!, $memberNo: Int!) {
              setMemberNo(memberId: $memberId, memberNo: $memberNo) {
                id
                memberNo
              }
            }
          `,
          variables: {
            memberId: memberId || id,
            memberNo: state.memberNo
          }
        });
        setMessage({
          type: 'success',
          content: `${formData.getIn('member.firstName'.split('.'))} ${formData.getIn(
            'member.lastName'.split('.')
          )} has been ${editMode ? 'updated' : 'created'}!`,
          show: true
        });
        onSubmit && onSubmit(formData);
      } catch (err) {
        setIsLoading(false);
        const eMsg = err.message.includes('Error Code: ValidationException')
          ? 'Please fill up all required fields.'
          : err.message;
        setMessage({ type: 'error', content: eMsg, show: true });
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    console.log('formData', formData.toJS()); //TRACE
  }, [formData]);

  useEffect(() => {
    if (!memberId) return;
    if (!memberModel) return;
    const {
      cognitoUser,
      manager,
      role,
      store,
      memberNo,
      ...restOfMemberData
    } = memberModel;
    let memberDataBasic = omit(restOfMemberData, [
      '__typename',
      'avatar',
      'contactInfo.__typename'
    ]);

    const memEmail = get(cognitoUser, 'Email') || restOfMemberData.email;

    //refactor contactinfo, remove once done
    memberDataBasic = {
      ...memberDataBasic,
      ...get(memberDataBasic, 'contactInfo', {}),
      email: memEmail
    };
    //end
    setFormData(oldFormData =>
      oldFormData.merge({
        member: Map({
          ...memberDataBasic,
          memberRoleId: get(role, 'id'),
          memberMembersId: get(manager, 'id', ''),
          memberStoreId: store.id
        }),
        user: Map({
          email: memEmail,
          group: get(role, 'group')
        })
      })
    );
    setState(oldState => ({ ...oldState, memberNo }));
  }, [memberId, memberModel]);

  useEffect(() => {
    if (currentUser.memberInfo.role.group !== 'Admins') {
      setFormData(oldFormData =>
        oldFormData.set(
          'member.memberStoreId',
          get(currentUser, 'memberInfo.store.id', '')
        )
      );
    }
    if (!editMode) createTempPassword();
  }, [editMode, currentUser]);

  useEffect(() => {
    if (disableManager) {
      setFormData(oldFormData => oldFormData.deleteIn(['member', 'memberMembersId']));
    }
  }, [disableManager]);

  function createFormFieldProps(
    fieldPath
    // isControlled
  ) {
    const val = formData.getIn(fieldPath.split('.'), '');
    const fProps = {
      id: fieldPath,
      name: fieldPath,
      value: val,
      disabled: isFetchingMemberData,
      onChange: handlers.onFieldChange
    };
    return fProps;
  }

  const progressProps = {};

  if (isFetchingMemberData) {
    progressProps.value = null;
    progressProps.variant = 'indeterminate';
  } else {
    progressProps.value = 0;
    progressProps.variant = 'determinate';
  }

  return (
    <>
      <LinearProgress {...progressProps} className={classes.progress} />
      <div style={{ textAlign: 'right', marginBottom: 10 }}>
        {!state.memberNo && memberId && (
          <Message
            variant="warning"
            content="No Member Number has been set"
            style={{ marginTop: 10, marginBottom: 20 }}
          />
        )}
        <label>Member No:</label>
        <TextField
          variant="outlined"
          style={{ width: 100, marginLeft: 15 }}
          value={state.memberNo || ''}
          onChange={e => {
            const value = e.target.value;
            setState(oldState => ({ ...oldState, memberNo: value }));
          }}
        />
      </div>
      <form onSubmit={handlers.handleSubmit} className={classes.root}>
        <TextField
          {...createFormFieldProps('member.firstName')}
          inputProps={{ required: true }}
          variant="outlined"
          label="First name"
          className={classes.firstName}
        />
        <TextField
          {...createFormFieldProps('member.lastName')}
          inputProps={{ required: true }}
          variant="outlined"
          label="Last name"
          className={classes.lastName}
        />
        <TextFieldWithTooltip
          inputProps={{ required: true }}
          type="email"
          variant="outlined"
          label="Email"
          {...createFormFieldProps('user.email')}
          disabled={editMode}
          tooltip="Disabled"
        />
        <TextField
          variant="outlined"
          select
          label="Role"
          {...createFormFieldProps('member.memberRoleId', true)}>
          {roles.map(item => (
            <MenuItem key={item.id} value={item.id}>
              {startCase(item.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          variant="outlined"
          select
          label="Employment Type"
          {...createFormFieldProps('member.employmentType', true)}>
          {employmentTypes.map(type => (
            <MenuItem key={type.name} value={type.name}>
              {capitalize(type.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextFieldWithTooltip
          variant="outlined"
          select
          label={`${disableManager ? 'No ' : ''}Manager`}
          {...createFormFieldProps('member.memberMembersId', true)}
          disabled={disableManager}
          tooltip="This user's role can't have a manager">
          {managers.map(manager => (
            <MenuItem key={manager.id} value={manager.id}>
              {`${startCase(manager.firstName)} ${startCase(manager.lastName)}`}
            </MenuItem>
          ))}
        </TextFieldWithTooltip>
        {disableStore ? (
          <center>
            <Typography>All Stores</Typography>
          </center>
        ) : (
          <TextField
            disabled={disableStore}
            variant="outlined"
            select
            label="Store"
            {...createFormFieldProps('member.memberStoreId', true)}>
            {stores.map(store => (
              <MenuItem key={store.id} value={store.id}>
                {startCase(store.name)}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          variant="outlined"
          select
          label="Status"
          {...createFormFieldProps('member.status', true)}>
          {employmentStatus.map(status => (
            <MenuItem key={status.name} value={status.name}>
              {capitalize(status.name)}
            </MenuItem>
          ))}
        </TextField>
        <Button
          disabled={isLoading || isFetchingMemberData}
          size="large"
          classes={{ root: classes.actionButton }}
          type="submit"
          variant="contained"
          color="secondary">
          {editMode ? 'Update' : 'Create'} User
        </Button>
        {editMode && (
          <StatusButton
            disabled={isLoading || isFetchingMemberData}
            classes={{ root: classes.actionButton }}
            memberData={formData.get('member', Map({}))}
          />
        )}
      </form>
    </>
  );
};

export default withStyles(styles)(UserForm);
